import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { getCampaignPlatformIds } from '../../../services/campaign.service';

/**
 * Manages platform selection within a campaign editor panel; provides UI bindings for expanding/collapsing the section, selecting platforms, validating selection, and emitting changes to the parent.
 * @component
 * @selector app-platform
 * @example
 *   <app-platform
 *     [isExpanded]="true"
 *     [campaignData]="campaign"
 *     [isCompleted]="false"
 *     (toggle)="onToggle($event)"
 *     (done)="onDone()"
 *     (dataChange)="onDataChange($event)">
 *   </app-platform>
 * @input {boolean} isExpanded - Controls whether the collapsible platform section is expanded (default: false).
 * @input {any} campaignData - Existing campaign payload used to initialize the selected platforms (expects a .platform array).
 * @input {boolean} isCompleted - Indicates whether the overall step is marked completed from the parent.
 * @output {EventEmitter<boolean>} toggle - Emitted when the collapsible section is toggled; payload is the new expanded state.
 * @output {EventEmitter<void>} done - Emitted when the user confirms the platform selection and validation passes.
 * @output {EventEmitter<any>} dataChange - Emitted when the selected platforms change; payload contains { platform: string[] }.
 *
 * Key internal state:
 * - selectedPlatforms: string[] - Current list of selected platform identifiers.
 * - showValidationError: boolean - Flag used to show validation UI when no platform is selected.
 * - messageService: NotificationService (injected) - Used for user messaging (injected via Angular inject()).
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Initializes selectedPlatforms from campaignData.platform if available when the component is created.
 * - ngOnChanges(): Updates selectedPlatforms when campaignData input changes.
 *
 * Key methods:
 * - onPlatformToggle(expanded: boolean): Emits the toggle event to inform the parent of expand/collapse actions.
 * - onPlatformDone(): Validates that at least one platform is selected; if valid it clears validation state and emits done(), otherwise sets showValidationError to true.
 * - onPlatformChange(): Clears validation error when a selection is made, constructs a data object { platform: selectedPlatforms } and emits it via dataChange.
 * - get isPlatformValid(): boolean - Convenience getter that returns true when at least one platform is selected.
 *
 * Usage notes:
 * - Provide campaignData.platform as an array to pre-populate selections.
 * - Listen to dataChange to keep parent state in sync as the user modifies selections.
 */
@Component({
  selector: 'app-platform',
  imports: [CommonModule, CollapsibleSectionComponent, MatCheckboxModule],
  templateUrl: './platform.component.html',
  styleUrl: './platform.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PlatformComponent implements OnInit, OnChanges {
  @Input() isExpanded = false;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();

  platformOptions = [
    { id: 'email-platform', value: 'email', label: 'Email', iconType: 'email' },
    { id: 'sms-platform', value: 'sms', label: 'Text Messaging', iconType: 'sms' },
    { id: 'facebook-platform', value: 'facebook', label: 'Facebook', iconType: 'facebook' },
    { id: 'google-ads-platform', value: 'google', label: 'Google Ads', iconType: 'google' },
    {
      id: 'physical-mailer-platform',
      value: 'physical_mailer',
      label: 'Physical Mailer',
      iconType: 'mailer',
    },
  ];

  selectedPlatforms: string[] = [];
  showValidationError = false;
  private messageService = inject(NotificationService);

  ngOnInit(): void {
    this.syncFromCampaignData();
  }

  ngOnChanges(): void {
    this.syncFromCampaignData();
  }

  private syncFromCampaignData(): void {
    if (this.campaignData?.platform != null) {
      // Legacy API used `mailer`; UI checkbox value is `physical_mailer`.
      const normalized = getCampaignPlatformIds(this.campaignData.platform).map(id =>
        id === 'mailer' ? 'physical_mailer' : id
      );
      this.selectedPlatforms = [...new Set(normalized)];
    }
    if (!Array.isArray(this.selectedPlatforms)) {
      this.selectedPlatforms = [];
    }
  }

  isPlatformSelected(platformValue: string): boolean {
    return this.selectedPlatforms.includes(platformValue);
  }

  onPlatformSelectionToggle(platformValue: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedPlatforms.includes(platformValue)) {
        this.selectedPlatforms = [...this.selectedPlatforms, platformValue];
      }
    } else {
      this.selectedPlatforms = this.selectedPlatforms.filter(value => value !== platformValue);
    }
    this.onPlatformChange();
  }

  onPlatformToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onPlatformDone(): void {
    if (this.selectedPlatforms.length === 0) {
      this.showValidationError = true;

      return;
    }
    this.showValidationError = false;
    this.done.emit();
  }

  onPlatformChange(): void {
    // PrimeNG checkbox updates ngModel after onChange; emit on next microtask.
    queueMicrotask(() => this.emitPlatformChange());
  }

  private emitPlatformChange(): void {
    if (this.selectedPlatforms.length > 0) {
      this.showValidationError = false;
    }
    this.dataChange.emit({
      platform: [...this.selectedPlatforms],
    });
  }

  get isPlatformValid(): boolean {
    return this.selectedPlatforms.length > 0;
  }

  trackByPlatformValue(_: number, item: { value: string }): string {
    return item.value;
  }
}
